import {
    ApolloClient,
    ApolloQueryResult,
    createHttpLink,
    DocumentNode,
    gql,
    InMemoryCache,
    TypedDocumentNode,
    useQuery
} from '@apollo/client';
import { isEmpty } from 'lodash';
import { useContext } from 'react';
import { ContactInput } from 'sr-types/lib/metadata/v1/graphql';
import {
    AlternateIdInput,
    Organization as Org,
    QuerySearchSocialProfileArgs,
    SearchSocialProfileResponse,
    Security
} from 'sr-types/lib/organization/v1/graphql';
import { Resource } from 'sr-types/lib/production/v1/graphql';
import { Organization as SearchOrg } from 'sr-types/lib/search/v1/graphql';
import { authLink, UserProfile } from '../common/auth/api';
import { UserContext } from '../common/auth/UserContext';
import { AlternateIdValidationInput } from '../common/components/AlternateIdForm';
import { AddressFragment, Location, LOCATION_FIELDS } from '../common/components/location';
import { findAlternateId } from '../common/components/utils/alternateIdFormUtils';
import { STUDIO_SYSTEM_LABEL } from '../common/constants/alternateIdConstants';
import { DEFAULT_URL, FETCH_POLICY_CONFIG } from '../common/constants/constants';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import Validator from '../common/form/Validator';
import { ReferenceFragment } from '../common/list/fragments';
import { searchClient } from '../common/list/slimQuery';
import { Reference } from '../common/reference/reference';
import { areAllFiltersInList, failedToastMessage, successToastMessage } from '../common/utils/commonUtils';
import { extractNames } from '../production/helpers/peopleUtils';
import { ContactFragment } from '../supply/opportunity/orders/order';
import { ORGANIZATION_TYPES } from './common/organizationConstants';
import { OrgTypeWithOneLocation } from './OrganizationProfileEditor';

export const client = new ApolloClient({
    link: authLink.concat(
        createHttpLink({
            uri: '/organization/v1/'
        })
    ),
    cache: new InMemoryCache({
        addTypename: false
    })
});

export const EMPTY_STATE = {
    publicName: undefined,
    organizationTypes: [],
    categories: [],
    hierarchicalCategories: [],
    isSharedInDirectory: false,
    locations: [],
    summary: {
        about: '',
        headline: ''
    },
    contact: undefined,
    website: '',
    websiteURL: DEFAULT_URL,
    reference: undefined,
    parentOrganization: undefined,
    contactLocation: undefined,
    security: undefined
};

export const EMPTY_ORG_ALTERNATE_ID = {
    StudioSystem: '',
    studioSystemAlternateId: undefined
};

// Fragments for shared fields
const OrganizationDetailsFields = gql`
    fragment OrganizationDetailsFields on Organization {
        publicName
        isSharedInDirectory
        summary {
            headline
            about
        }
        locations {
            ...LocationFields
            timezone {
                id
                name
            }
        }
        contact {
            ...Contact
        }
        branding {
            useCustom
            brandNameOrText
            smallLogo {
                ...Reference
            }
            bigLogo {
                ...Reference
            }
        }
        address {
            ...AddressFields
            label
        }
        organizationTypes
        parentOrganization {
            label
            reference {
                ...Reference
            }
        }
        categories
        hierarchicalCategories {
            lineage {
                value
            }
            value
        }
        website
        websiteURL
        reference {
            ...Reference
        }
        security {
            allowedActions
        }
        alternateIds {
            label
            value
            url
        }
    }
    ${LOCATION_FIELDS}
    ${AddressFragment}
    ${ReferenceFragment}
    ${ContactFragment}
`;

export const productServiceNoteReferenceQuery: DocumentNode = gql`
    query ProductServiceNoteReference($id: ID!) {
        organization(id: $id) {
            ...OrganizationDetailsFields
            productServiceNoteReference {
                ...Reference
            }
        }
    }
    ${ReferenceFragment}
    ${OrganizationDetailsFields}
`;

export const detailsQuery: DocumentNode = gql`
    query Organization($id: ID!) {
        organization(id: $id) {
            ...OrganizationDetailsFields
        }
    }
    ${OrganizationDetailsFields}
`;

export type Organization = {
    publicName: string;
    organizationTypes: Array<string>;
    categories: Array<string>;
    hierarchicalCategories: Array<Category>;
    locations: Array<Location>;
    isSharedInDirectory: boolean;
    summary: {
        about: string;
        headline: string;
    };
    website: string;
    websiteURL: string;
    reference: object;
    parentOrganization: Reference;
    contactLocation: Location;
    contact: Array<ContactInput>;
    security: Security;
    address?: {
        label?: string;
        [key: string]: any;
    };
};

interface CommonExtendedFields {
    StudioSystem: string;
    studioSystemAlternateId: AlternateIdInput;
}
export interface OrganizationEditorInput extends Organization, CommonExtendedFields {}

export const extractOrgDetailsFromSearchQuery = (organization: SearchOrg): { Name: string; Location: string } => {
    const { name, location } = organization;
    return {
        Name: name,
        Location: location
    };
};

export const rowToOrganization = (rows, colDefs = [], activeOrganizationAccount = '') => {
    return rows.map((row) => {
        const organizationEntity = {
            publicName: row.name,
            organizationTypes: [row.type],
            hierarchicalCategories: [
                {
                    value: row.category ? row.category : '',
                    lineage: {
                        value: row.category ? row.category : ''
                    }
                }
            ],
            locations: row.organizationLocations ? [row.organizationLocations] : [],
            summary: {
                about: row.about ? row.about : '',
                headline: row.headline ? row.headline : ''
            },
            parentOrganization: {
                reference: null,
                label: row.parentOrganization ? row.parentOrganization : ''
            },
            website: '',
            websiteURL: row.website,
            branding: {
                brandNameOrText: row.brandname ? row.brandname : ''
            },
            isSharedInDirectory: row.directory && row.directory.toLowerCase() === 'true' ? true : false
        };
        return organizationEntity;
    });
};

type Category = {
    value: string;
    lineage: {
        value: string;
    };
};

export const saveQuery: DocumentNode = gql`
    mutation SaveOrganization($input: OrganizationInput!) {
        saveOrganization(input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const bulkSaveQuery: DocumentNode = gql`
    mutation BulkSaveOrganization($input: BulkSaveOrganizationRequest!) {
        bulkSaveOrganization(input: $input) {
            createdCount
            updatedCount
            unprocessedCount
            results {
                id
                errors {
                    field
                    message
                }
                reference {
                    ...Reference
                }
            }
            logFile {
                id
                uriPrefix
            }
        }
    }
    ${ReferenceFragment}
`;

export const deleteQuery: DocumentNode = gql`
    mutation DeleteOrganization($id: ID!) {
        deleteOrganization(id: $id) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const autoCreateQuery: DocumentNode = gql`
    mutation AutoCreateOrganization($input: OrganizationInput!) {
        autoCreateOrganization(input: $input) {
            id
            reference {
                id
                eventId
                type
                uriPrefix
                deleted
            }
            errors {
                field
                message
            }
        }
    }
`;

export const inactivateMutation: DocumentNode = gql`
    mutation InactivateOrganization($id: ID!) {
        inactivateOrganization(id: $id) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const activateMutation: DocumentNode = gql`
    mutation ActivateOrganization($id: ID!) {
        activateOrganization(id: $id) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const shareOrganizationInDirectoryQuery: DocumentNode = gql`
    mutation ShareOrganizationInDirectory($id: ID!) {
        shareOrganizationInDirectory(id: $id) {
            id
            errors {
                field
                key
                message
            }
        }
    }
`;

export const mergeAnnotationMutation: DocumentNode = gql`
    mutation MergeAnnotation($orgId: ID!, $annotationId: ID!) {
        mergeAnnotation(orgId: $orgId, annotationId: $annotationId) {
            id
            errors {
                field
                key
                message
            }
        }
    }
`;

export const useGetOrganizationsDetails = (orgId: string, skip = undefined, query = detailsQuery) => {
    const { activeOrganizationAccount } = useContext(UserContext);

    const {
        data: organizationDetails,
        loading: organizationDetailsLoading,
        refetch: organizationDetailsRefetch
    } = useQuery(query, {
        variables: { id: orgId },
        client,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        skip: skip,
        notifyOnNetworkStatusChange: true
    });

    return { organizationDetails, organizationDetailsLoading, organizationDetailsRefetch };
};

export const organizationLineItemSave = (
    savedEntity,
    save,
    setIsFormSaving,
    setOrganizationModalState,
    lineItemValues,
    setLineItemValues,
    onAddToShortlist = null
) => {
    const { isSharedInDirectory, ...rest } = savedEntity;
    save({
        variables: {
            input: { ...rest }
        }
    })
        .then((res) => {
            if (res.data?.autoCreateOrganization?.errors || res.data?.saveOrganization?.errors) {
                failedToastMessage('form.save.failure', { name: savedEntity?.publicName });
                setIsFormSaving(false);
            } else {
                const inputName = extractNames(savedEntity?.publicName);
                const id = res.data?.autoCreateOrganization?.id
                    ? res.data.autoCreateOrganization.id
                    : res.data?.saveOrganization?.id;

                setLineItemValues({
                    ...lineItemValues,
                    itemInfo: {
                        ref: {
                            id,
                            label: `${inputName.firstName} ${inputName.lastName}`,
                            type: 'Organization'
                        },
                        label: `${inputName.firstName} ${inputName.lastName}`
                    },
                    name: `${inputName.firstName} ${inputName.lastName}`
                });
                if (onAddToShortlist) {
                    onAddToShortlist({
                        name: savedEntity.publicName,
                        reference: {
                            id,
                            label: savedEntity.publicName,
                            type: 'Organization'
                        },
                        id,
                        contact: savedEntity.contact
                    });
                }

                successToastMessage('form.save.success', { name: savedEntity?.publicName });
                setIsFormSaving(false);
                setOrganizationModalState(false);
            }
        })
        .catch((err) => {
            failedToastMessage('form.save.failure', { name: savedEntity?.publicName });
            setIsFormSaving(false);
        })
        .finally(() => {
            searchClient.refetchQueries({
                include: ['slimResultsQuery'],
                onQueryUpdated(observableQuery) {
                    return areAllFiltersInList(observableQuery.options.variables.filters, [
                        {
                            identifier: 'entity',
                            value: 'Organization'
                        }
                    ]);
                }
            });
        });
};

export const onActivateOrInactivateHandle = (
    org,
    isInactivate,
    isActivate,
    activeOrganizationAccount,
    activate,
    inactivate
) => {
    return new Promise<void>((resolve, reject) => {
        if (isInactivate) {
            inactivate({
                variables: {
                    id: org.id
                },
                context: {
                    headers: {
                        ownerId: activeOrganizationAccount
                    }
                }
            })
                .then((res) => {
                    if (res.data.inactivateOrganization.error) {
                        failedToastMessage('organization.inactivate.failure');
                        reject();
                    } else {
                        successToastMessage('organization.inactivate.success');

                        resolve();
                    }
                })
                .catch(() => {
                    failedToastMessage('organization.inactivate.failure');
                    reject();
                });
        }
        if (isActivate) {
            activate({
                variables: {
                    id: org.id
                },
                context: {
                    headers: {
                        ownerId: activeOrganizationAccount
                    }
                }
            })
                .then((res) => {
                    if (res.data.activateOrganization.error) {
                        failedToastMessage('organization.activate.failure');
                        reject();
                    } else {
                        successToastMessage('organization.activate.success');
                        resolve();
                    }
                })
                .catch(() => {
                    failedToastMessage('organization.activate.failure');
                    reject();
                });
        }
    });
};

export const checkForShowrunnrAccount = (activeOrganizationAccount: string, userProfile: UserProfile) => {
    const showrunnrAccount = userProfile.memberships.find((m) => m.name === 'Showrunnr');
    if (showrunnrAccount && showrunnrAccount.id === activeOrganizationAccount) {
        return true;
    }
    return false;
};

export const inputEntityForQuickAddOrg = (organizationState: Organization, resourceData: Resource) => {
    return {
        ...organizationState,
        contact: organizationState?.contact?.length ? organizationState.contact[0] : undefined,
        hierarchicalCategories: resourceData?.resourceCategory
            ? [
                  {
                      lineage: { value: resourceData?.resourceCategory },
                      value: resourceData?.resourceCategory
                  }
              ]
            : null,
        locations: resourceData?.locations || organizationState.locations,
        websiteURL: ''
    };
};

export const showLocationForQuickAddOrg = (orgState: Organization, resourceData: Resource) => {
    return !isEmpty(resourceData)
        ? isEmpty(resourceData.locations) && orgState.organizationTypes?.includes(ORGANIZATION_TYPES.campus)
        : orgState.organizationTypes?.includes(ORGANIZATION_TYPES.campus);
};

export const validationRulesForQuickAddOrg = (orgState?: Organization, resourceData?: Resource) => {
    const validationRules = {
        publicName: [Validator.RULES.isRequired],
        contact: [Validator.RULES.isRequired]
    };

    if (orgState && resourceData && showLocationForQuickAddOrg(orgState, resourceData)) {
        validationRules['locations'] = [Validator.RULES.isRequired];
    }
    return validationRules;
};

export const useCreateOrganizationWithInput = (saveOrganization: any) => {
    return (inputValue) => {
        return new Promise((resolve, reject) => {
            saveOrganization({
                variables: {
                    input: {
                        ...EMPTY_STATE,
                        publicName: inputValue,
                        websiteURL: ''
                    }
                }
            })
                .then((res) => {
                    const { autoCreateOrganization } = res.data;
                    const updatedCreateOption = {
                        name: inputValue,
                        reference: autoCreateOrganization.reference
                    };
                    resolve(updatedCreateOption);
                    successToastMessage('entity.create.success', { name: ENTITY_CONSTANTS.organization });
                })
                .catch((err) => {
                    reject(err);
                    failedToastMessage('entity.create.failure', { name: ENTITY_CONSTANTS.organization });
                });
        });
    };
};

export const searchSocialProfile: TypedDocumentNode<SearchSocialProfileResponse, QuerySearchSocialProfileArgs> = gql`
    query SearchSocialProfile($input: SearchSocialProfileInput!) {
        searchSocialProfile(input: $input) {
            errors {
                field
                key
                message
            }
            alternateId {
                label
                value
                url
            }
        }
    }
`;

export const getValidationErrorsForOrganization = ({
    profileId,
    profileUrl,
    entityId,
    ownerId
}: AlternateIdValidationInput): Promise<ApolloQueryResult<SearchSocialProfileResponse>> => {
    return client.query({
        query: searchSocialProfile,
        variables: {
            input: {
                profileUrl,
                profileId,
                organizationId: entityId
            }
        },
        context: {
            headers: {
                ownerId
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy
    });
};

export const extractOrgInfoForPreview = (organization: Org) => {
    // to avoid undefined label error in LocationsInput
    const { locations } = organization;
    const contactLocation = organization?.address
        ? {
              ...organization.address,
              label: organization.address.label
          }
        : undefined;
    const contact = organization.contact ? [organization.contact] : [];
    // Assign object/array based on where the incoming orgType supports single or mutiple locations
    const updatedLocations = organization.organizationTypes?.some((t) => OrgTypeWithOneLocation.includes(t))
        ? locations[0]
        : locations;

    let studioSystem;
    if (organization.alternateIds?.length) {
        studioSystem = findAlternateId(organization.alternateIds, STUDIO_SYSTEM_LABEL);
    }
    return {
        ...organization,
        studioSystemAlternateId: studioSystem,
        StudioSystem: studioSystem?.url || '',
        locations: updatedLocations ? updatedLocations : [],
        contact,
        contactLocation
    } as OrganizationEditorInput;
};
